// @ts-nocheck
/**
 * A/B Test Configuration Wizard Component
 * Carbon Design System wizard for creating and managing A/B tests
 */

import React, { useState } from 'react';
import {
  ProgressIndicator,
  ProgressStep,
  Button,
  Form,
  TextInput,
  TextArea,
  DatePicker,
  DatePickerInput,
  NumberInput,
  Select,
  SelectItem,
  Grid,
  Column,
  Tile,
  Tag,
  Modal,
  Slider,
} from '@carbon/react';
import { ChevronLeft, ChevronRight, Launch, Analytics } from '@carbon/icons-react';

import type {
  ABTestWizardState,
  ABTestConfig,
  ABTestVariant,
  WinnerCriteria,
} from '../../../shared/interfaces/ContentManagementAdmin';

interface ABTestConfigWizardProps {
  contentId?: string;
  onTestCreate: (config: ABTestConfig) => void;
  onTestUpdate: (testId: string, config: ABTestConfig) => void;
}

const ABTestConfigWizard: React.FC<ABTestConfigWizardProps> = ({ contentId, onTestCreate, onTestUpdate }) => {
  const [state, setState] = useState<ABTestWizardState>({
    currentStep: 0,
    totalSteps: 4,
    testConfig: {
      name: '',
      description: '',
      contentId: contentId || '',
      startDate: new Date().toISOString(),
      trafficAllocation: 50,
      winnerCriteria: {
        metric: 'conversion_rate',
        minimumSampleSize: 1000,
        confidenceLevel: 95,
        minimumDetectableDifference: 5,
      },
      tags: [],
    },
    variants: [
      {
        id: '1',
        name: 'Control',
        description: 'Original version',
        trafficPercentage: 50,
        contentVersionId: '',
        isControl: true,
      },
      {
        id: '2',
        name: 'Variant A',
        description: 'Test version',
        trafficPercentage: 50,
        contentVersionId: '',
        isControl: false,
      },
    ],
    validation: [],
    isValid: false,
  });

  const steps = ['Test Setup', 'Variants Configuration', 'Success Criteria', 'Review & Launch'];

  const handleNext = () => {
    if (state.currentStep < state.totalSteps - 1) {
      setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
    }
  };

  const handlePrevious = () => {
    if (state.currentStep > 0) {
      setState(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
    }
  };

  const handleTestConfigChange = (field: string, value: any) => {
    setState(prev => ({
      ...prev,
      testConfig: { ...prev.testConfig, [field]: value },
    }));
  };

  const handleVariantChange = (variantId: string, field: string, value: any) => {
    setState(prev => ({
      ...prev,
      variants: prev.variants.map(variant => (variant.id === variantId ? { ...variant, [field]: value } : variant)),
    }));
  };

  const renderStepContent = () => {
    switch (state.currentStep) {
      case 0: // Test Setup
        return (
          <Grid>
            <Column lg={8} md={6} sm={4}>
              <TextInput
                id="test-name"
                labelText="Test Name"
                placeholder="Enter A/B test name"
                value={state.testConfig.name}
                onChange={e => handleTestConfigChange('name', e.target.value)}
              />
            </Column>
            <Column lg={8} md={6} sm={4}>
              <TextArea
                id="test-description"
                labelText="Description"
                placeholder="Describe what you're testing"
                rows={3}
                value={state.testConfig.description}
                onChange={e => handleTestConfigChange('description', e.target.value)}
              />
            </Column>
            <Column lg={8} md={6} sm={4}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  Traffic Allocation ({state.testConfig.trafficAllocation}%)
                </label>
                <Slider
                  labelText=""
                  min={10}
                  max={100}
                  step={10}
                  value={state.testConfig.trafficAllocation}
                  onChange={({ value }) => handleTestConfigChange('trafficAllocation', value)}
                />
              </div>
            </Column>
          </Grid>
        );

      case 1: // Variants Configuration
        return (
          <div>
            <h4 style={{ marginBottom: '1rem' }}>Test Variants</h4>
            {state.variants.map(variant => (
              <Tile key={variant.id} style={{ marginBottom: '1rem' }}>
                <Grid>
                  <Column lg={4} md={3} sm={2}>
                    <TextInput
                      id={`variant-name-${variant.id}`}
                      labelText="Variant Name"
                      value={variant.name}
                      onChange={e => handleVariantChange(variant.id, 'name', e.target.value)}
                    />
                  </Column>
                  <Column lg={4} md={3} sm={2}>
                    <NumberInput
                      id={`variant-traffic-${variant.id}`}
                      label="Traffic %"
                      value={variant.trafficPercentage}
                      onChange={e => handleVariantChange(variant.id, 'trafficPercentage', e.target.value)}
                      min={0}
                      max={100}
                    />
                  </Column>
                  <Column lg={8} md={6} sm={4}>
                    <TextArea
                      id={`variant-desc-${variant.id}`}
                      labelText="Description"
                      value={variant.description}
                      onChange={e => handleVariantChange(variant.id, 'description', e.target.value)}
                      rows={2}
                    />
                  </Column>
                </Grid>
                {variant.isControl && <Tag type="blue">Control</Tag>}
              </Tile>
            ))}
          </div>
        );

      case 2: // Success Criteria
        return (
          <Grid>
            <Column lg={6} md={4} sm={2}>
              <Select
                id="success-metric"
                labelText="Success Metric"
                value={state.testConfig.winnerCriteria.metric}
                onChange={e =>
                  handleTestConfigChange('winnerCriteria', {
                    ...state.testConfig.winnerCriteria,
                    metric: e.target.value,
                  })
                }
              >
                <SelectItem value="conversion_rate" text="Conversion Rate" />
                <SelectItem value="click_through_rate" text="Click Through Rate" />
                <SelectItem value="engagement_time" text="Engagement Time" />
                <SelectItem value="custom" text="Custom Metric" />
              </Select>
            </Column>
            <Column lg={6} md={4} sm={2}>
              <NumberInput
                id="confidence-level"
                label="Confidence Level (%)"
                value={state.testConfig.winnerCriteria.confidenceLevel}
                onChange={e =>
                  handleTestConfigChange('winnerCriteria', {
                    ...state.testConfig.winnerCriteria,
                    confidenceLevel: e.target.value,
                  })
                }
                min={80}
                max={99}
              />
            </Column>
            <Column lg={6} md={4} sm={2}>
              <NumberInput
                id="sample-size"
                label="Minimum Sample Size"
                value={state.testConfig.winnerCriteria.minimumSampleSize}
                onChange={e =>
                  handleTestConfigChange('winnerCriteria', {
                    ...state.testConfig.winnerCriteria,
                    minimumSampleSize: e.target.value,
                  })
                }
                min={100}
              />
            </Column>
            <Column lg={6} md={4} sm={2}>
              <NumberInput
                id="min-detectable-diff"
                label="Minimum Detectable Difference (%)"
                value={state.testConfig.winnerCriteria.minimumDetectableDifference}
                onChange={e =>
                  handleTestConfigChange('winnerCriteria', {
                    ...state.testConfig.winnerCriteria,
                    minimumDetectableDifference: e.target.value,
                  })
                }
                min={1}
                max={50}
              />
            </Column>
          </Grid>
        );

      case 3: // Review & Launch
        return (
          <div>
            <h4 style={{ marginBottom: '1rem' }}>Test Summary</h4>
            <Tile>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Test Name:</strong> {state.testConfig.name}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Description:</strong> {state.testConfig.description}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Traffic Allocation:</strong> {state.testConfig.trafficAllocation}%
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Variants:</strong>
                <ul>
                  {state.variants.map(variant => (
                    <li key={variant.id}>
                      {variant.name} ({variant.trafficPercentage}%)
                      {variant.isControl && (
                        <Tag type="blue" size="sm">
                          Control
                        </Tag>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Success Metric:</strong> {state.testConfig.winnerCriteria.metric.replace('_', ' ')}
              </div>
              <div>
                <strong>Confidence Level:</strong> {state.testConfig.winnerCriteria.confidenceLevel}%
              </div>
            </Tile>
          </div>
        );

      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <div className="ab-test-config-wizard">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
        }}
      >
        <div>
          <h2>A/B Test Configuration</h2>
          <p style={{ color: 'var(--cds-text-02)' }}>Create and configure A/B tests for your content</p>
        </div>
      </div>

      {/* Progress Indicator */}
      <ProgressIndicator currentIndex={state.currentStep} spaceEqually style={{ marginBottom: '2rem' }}>
        {steps.map((step, index) => (
          <ProgressStep
            key={index}
            label={step}
            complete={index < state.currentStep}
            current={index === state.currentStep}
          />
        ))}
      </ProgressIndicator>

      {/* Step Content */}
      <div style={{ minHeight: '400px', marginBottom: '2rem' }}>{renderStepContent()}</div>

      {/* Navigation Buttons */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid var(--cds-border-subtle)',
          paddingTop: '1rem',
        }}
      >
        <Button kind="secondary" renderIcon={ChevronLeft} onClick={handlePrevious} disabled={state.currentStep === 0}>
          Previous
        </Button>

        <div style={{ display: 'flex', gap: '1rem' }}>
          {state.currentStep === state.totalSteps - 1 ? (
            <Button kind="primary" renderIcon={Launch} onClick={() => onTestCreate(state.testConfig)}>
              Launch Test
            </Button>
          ) : (
            <Button kind="primary" renderIcon={ChevronRight} iconDescription="Next" onClick={handleNext}>
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ABTestConfigWizard;
